import { $api } from "./api-client";
import type { components } from "./generated-api-types";

export type FeatureFlags = components["schemas"]["FeatureFlags"];

/**
 * Used until the query resolves, and if it fails outright. Not a deployment
 * default — those are the `FEATURE_*` env vars the API reads.
 */
const FALLBACK_FLAGS: FeatureFlags = {
  avatars3d: false,
  avatarsLiveavatar: false,
  voiceAuth: false,
};

/**
 * What `GET /api/config/feature-flags` returns: only flags a page needs before
 * login (the list lives in `services/core/backend/config/feature_flags.py`). Narrower than
 * `FeatureFlags`, so reading a flag it doesn't send is a compile error.
 */
export type PublicFeatureFlags = components["schemas"]["PublicFeatureFlags"];

/** Same, for the pre-login subset. */
const PUBLIC_FALLBACK_FLAGS: PublicFeatureFlags = {
  voiceAuth: false,
};

/** Deployment-wide values, no session needed. Used by the voice-auth flow. */
export function useFeatureFlags(): PublicFeatureFlags {
  const { data } = $api.useQuery(
    "get",
    "/api/config/feature-flags",
    {},
    { staleTime: Infinity },
  );
  return data ?? PUBLIC_FALLBACK_FLAGS;
}

export function useFeatureFlagsLoading(): boolean {
  const { isLoading } = $api.useQuery(
    "get",
    "/api/config/feature-flags",
    {},
    { staleTime: Infinity },
  );
  return isLoading;
}

/**
 * Every flag, evaluated for the logged-in user. Cached for the page session:
 * these gate nav entries and routes, so a Bucketeer change waits for a reload.
 */
export function useUserFeatureFlags(): FeatureFlags {
  const { data } = $api.useQuery(
    "get",
    "/api/config/feature-flags/user",
    {},
    { staleTime: Infinity },
  );
  return data ?? FALLBACK_FLAGS;
}

export function useUserFeatureFlagsLoading(): boolean {
  const { isLoading } = $api.useQuery(
    "get",
    "/api/config/feature-flags/user",
    {},
    { staleTime: Infinity },
  );
  return isLoading;
}
